import React from "react";
import { useQuery, gql } from "@apollo/client";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@material-ui/core";

import { KeyValueArray } from "./KeyValueArray";

const GET_COLLEGE_INFO = gql`
  query Query($getCollegeInfoCollegeId: String) {
    getCollegeInfo(collegeId: $getCollegeInfoCollegeId)
  }
`;

export function Finance() {
  const { collegeId } = useParams();

  const { loading, error, data } = useQuery(GET_COLLEGE_INFO, {
    fetchPolicy: "cache-first",
    variables: {
      getCollegeInfoCollegeId: collegeId,
    },
  });

  if (loading) {
    return (
      <Box>
        <CircularProgress style={{ color: "#3699FF" }} />
      </Box>
    );
  }

  let collegeDetails = !error ? JSON.parse(data.getCollegeInfo) : {};

  const mainInfo = [
    { key: "avgCostOfAttendance", value: collegeDetails.avgCostOfAttendance },
    { key: "avgNetPrice", value: collegeDetails.avgNetPrice },
    { key: "financialAidWebsite", value: collegeDetails.financialAidWebsite },
    { key: "inStateTuition", value: collegeDetails.inStateTuition },
    {
      key: "percentOfStudentsWhoReceiveFinancialAid",
      value:
        collegeDetails.percentOfStudentsWhoReceiveFinancialAid &&
        (collegeDetails.percentOfStudentsWhoReceiveFinancialAid * 100).toFixed(
          2
        ) + "%",
    },
    {
      key: "percentUndergradsAwardedAid",
      value: collegeDetails.percentUndergradsAwardedAid,
    },
  ];

  const awardedIncome = [
    {
      key: "averageAidAwardedHighIncome",
      value: collegeDetails.averageAidAwardedHighIncome,
    },
    {
      key: "averageAidAwardedLowIncome",
      value: collegeDetails.averageAidAwardedLowIncome,
    },
    {
      key: "averageAidAwardedLowerMiddleIncome",
      value: collegeDetails.averageAidAwardedLowerMiddleIncome,
    },
    {
      key: "averageAidAwardedMiddleIncome",
      value: collegeDetails.averageAidAwardedMiddleIncome,
    },
    {
      key: "averageAidAwardedUpperMiddleIncome",
      value: collegeDetails.averageAidAwardedUpperMiddleIncome,
    },
  ];

  const incomeLevel = [
    {
      key: "netPriceByIncomeLevel0To3000",
      title: "Net Price By Income Level 0 To 3000",
      value: collegeDetails.netPriceByIncomeLevel0To3000,
    },
    {
      key: "netPriceByIncomeLevel110001Plus",
      title: "Net Price By Income Level 110001 Plus",
      value: collegeDetails.netPriceByIncomeLevel110001Plus,
    },
    {
      key: "netPriceByIncomeLevel30001To48000",
      title: "Net Price By Income Level 30001 To 48000",
      value: collegeDetails.netPriceByIncomeLevel30001To48000,
    },
    {
      key: "netPriceByIncomeLevel48001To75000",
      title: "Net Price By Income Level 48001 To 75000",
      value: collegeDetails.netPriceByIncomeLevel48001To75000,
    },
    {
      key: "netPriceByIncomeLevel75001To110000",
      title: "Net Price By Income Level 75001 To 110000",
      value: collegeDetails.netPriceByIncomeLevel75001To110000,
    },
  ];

  const otherInfo = [
    { key: "federalLoanRate", value: collegeDetails.federalLoanRate },
    { key: "loanPrincipal", value: collegeDetails.loanPrincipal },
    {
      key: "medianEarningsSixYrsAfterEntry",
      value: collegeDetails.medianEarningsSixYrsAfterEntry,
    },
    {
      key: "medianEarningsTenYrsAfterEntry",
      value: collegeDetails.medianEarningsTenYrsAfterEntry,
    },
    { key: "outOfStateTuition", value: collegeDetails.outOfStateTuition },
    { key: "prepaidTuitionPlan", value: collegeDetails.prepaidTuitionPlan },
    {
      key: "rankingsBestValueColleges",
      value: collegeDetails.rankingsBestValueColleges,
    },
    { key: "tuitionGuaranteePlan", value: collegeDetails.tuitionGuaranteePlan },
    { key: "tuitionPaymentPlan", value: collegeDetails.tuitionPaymentPlan },
    {
      key: "typicalBooksAndSupplies",
      value: collegeDetails.typicalBooksAndSupplies,
    },
    { key: "typicalFinancialAid", value: collegeDetails.typicalFinancialAid },
    { key: "typicalMiscExpenses", value: collegeDetails.typicalMiscExpenses },
    { key: "typicalRoomAndBoard", value: collegeDetails.typicalRoomAndBoard },
    {
      key: "undergradApplicationFee",
      value: collegeDetails.undergradApplicationFee,
    },
  ];

  return (
    <>
      <Card className="mb-5">
        <CardContent>
          <Typography variant="h4" className="text-center" color="primary">
            {collegeDetails.name + " Admissions"}
          </Typography>
        </CardContent>
      </Card>

      <Card className="mb-5">
        <CardContent>
          <Grid container spacing={8}>
            <Grid item xs={12} sm={6}>
              <KeyValueArray
                data={mainInfo.slice(0, Math.ceil(mainInfo.length / 2))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <KeyValueArray
                data={mainInfo.slice(Math.ceil(mainInfo.length / 2))}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card className="mb-5">
        <CardContent>
          <Grid container spacing={8}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" className="text-center" color="primary">
                Awarded Income
              </Typography>
              <KeyValueArray data={awardedIncome} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" className="text-center" color="primary">
                Income Level
              </Typography>
              <KeyValueArray data={incomeLevel} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" className="text-center" color="primary">
            Other Info
          </Typography>
          <Grid container spacing={8}>
            <Grid item xs={12} sm={6}>
              <KeyValueArray
                data={otherInfo.slice(0, Math.floor(otherInfo.length / 2))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <KeyValueArray
                data={otherInfo.slice(Math.floor(otherInfo.length / 2))}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}
